import * as React from "react";
import { format, parseISO } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useNavigation, type CaptionProps } from "react-day-picker";
import { Button } from "@/frontend/components/ui/button";
import { Calendar } from "@/frontend/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/frontend/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/frontend/components/ui/select";
import { cn } from "@/frontend/lib/utils";

const FROM_YEAR = 1920;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Calendar caption with shadcn month + year dropdowns (replaces react-day-picker's
 * default caption, whose native selects overflow and render an oversized year
 * list). Used by BirthDatePicker so far-back dates are quick to reach.
 */
function MonthYearCaption({ displayMonth }: CaptionProps) {
  const { goToMonth } = useNavigation();
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear; y >= FROM_YEAR; y--) years.push(y);

  return (
    <div className="flex items-center justify-center gap-2 px-1">
      <Select
        value={String(displayMonth.getMonth())}
        onValueChange={(v) =>
          goToMonth(new Date(displayMonth.getFullYear(), Number(v), 1))
        }
      >
        <SelectTrigger className="h-8 w-[120px] text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          {MONTHS.map((m, idx) => (
            <SelectItem key={m} value={String(idx)}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={String(displayMonth.getFullYear())}
        onValueChange={(v) =>
          goToMonth(new Date(Number(v), displayMonth.getMonth(), 1))
        }
      >
        <SelectTrigger className="h-8 w-[88px] text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          {years.map((y) => (
            <SelectItem key={y} value={String(y)}>
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface BirthDatePickerProps {
  id?: string;
  /** ISO date 'yyyy-MM-dd'. */
  value?: string;
  onChange: (value: string) => void;
  invalid?: boolean;
}

/**
 * Date-of-birth picker: the standard shadcn Calendar in a Popover, with a
 * month/year dropdown caption and future dates disabled. Stores 'yyyy-MM-dd'.
 */
export function BirthDatePicker({
  id,
  value,
  onChange,
  invalid,
}: BirthDatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const selected = value ? parseISO(value) : undefined;
  const today = new Date();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-start pl-3 text-left font-normal hover:bg-transparent hover:text-foreground",
            !selected && "text-muted-foreground",
            invalid && "border-destructive"
          )}
        >
          {selected ? format(selected, "dd/MM/yyyy") : <span>Pick a date</span>}
          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected ?? new Date(2000, 0)}
          onSelect={(date) => {
            onChange(date ? format(date, "yyyy-MM-dd") : "");
            setOpen(false);
          }}
          disabled={(date) => date > today}
          fromYear={FROM_YEAR}
          toYear={today.getFullYear()}
          components={{ Caption: MonthYearCaption }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
